import { Divider } from '@/components/ui/Divider'
import DocumentTemplate from '@/components/ui/document-template/DocumentTemplate'

function Section({ children }: { children: React.ReactNode }) {
	return <section className="flex flex-col gap-5">{children}</section>
}

/*
	In order to handle special characters, we need to use the unicode escape sequence:
	\n - Line Feed
	\u00A0 - No-Break Space
	...
*/
const CONTENT = [
	{
		title: 'Are Entering Into a Binding Agreement',
		text: 'By accessing, using or interacting with the Interface in any way, including by connecting your digital wallet address to the Interface, you represent that you have read, understand, acknowledge, and agree that you are entering into a binding legal agreement with us that includes these Terms and the Privacy Policy in their entirety. If you do not agree to be bound, you are not authorized to access, and should not use, any of the Services.'
	},
	{
		title: 'Technical Services Only',
		text: 'THE SERVICES INCLUDE TECHNICAL AND ADMINISTRATIVE SERVICES THAT MAKE IT POSSIBLE FOR USERS TO ACCESS AND INTERACT WITH ON-CHAIN SMART CONTRACTS THAT FUNCTION DETERMINISTICALLY AND ARE NOT OPERATED OR CONTROLLED BY ENTERTAINMENT TRADING TECHNOLOGIES LTD., AND INCLUDE, AMONG OTHER THINGS, THE INTERFACE, WHICH ALLOWS USERS TO DIRECTLY INTERACT WITH THE FULLY NON-CUSTODIAL SET OF SMART CONTRACTS WHICH OPERATES ON A PERMISSIONLESS PUBLIC BLOCKCHAIN AND PROVIDE ONE, BUT NOT THE EXCLUSIVE, MEANS OF ACCESSING THE PEPPER SMART CONTRACTS. ENTERTAINMENT TRADING TECHNOLOGIES LTD. IS NOT A BROKER, DEALER, EXCHANGE, INVESTMENT ADVISER, CUSTODIAN OR FINANCIAL SERVICE PROVIDER OF ANY KIND. WE DO NOT HAVE A FIDUCIARY RELATIONSHIP OR OBLIGATION TO YOU IN CONNECTION WITH THE INTERFACE.'
	},
	{
		title: 'Dispute Resolution; Class Action and Jury Trial Waiver',
		text: 'PLEASE BE AWARE THAT SECTION 15 GOVERNS THE RESOLUTION OF DISPUTES BETWEEN YOU AND US. SECTION 15 REQUIRES THAT, WITH LIMITED EXCEPTIONS, ALL DISPUTES BETWEEN YOU AND US SHALL BE RESOLVED EXCLUSIVELY BY THE COURTS OF JUSTICE LOCATED IN MAHÉ, SEYCHELLES. SECTION 13 ALSO CONTAINS A CLASS ACTION AND JURY TRIAL WAIVER. PLEASE READ SECTION 15 CAREFULLY, AS ANY DISPUTES ARISING FROM OR RELATING TO THESE TERMS OR YOUR USE OF THE INTERFACE SHALL BE SUBJECT TO THE EXCLUSIVE JURISDICTION OF THE COURTS IN MAHÉ, SEYCHELLES.'
	},
	{
		title: 'Amendment of These Terms',
		text: 'Entertainment Trading Technologies Ltd. reserves the right, in our sole discretion, to amend or modify these Terms at any time. If and when we make changes, we will provide you with notice of such changes by updating the date reflected at the top of these Terms. All amendments will be effective when they are posted, and your continued use of the Services thereafter will constitute your acceptance of such revised Terms. If you do not agree with any such amendment, you must immediately stop accessing the Interface and terminate your use of the Services.'
	},
	{
		title: '1. Acceptance Of Terms',
		text: 'By accessing or using the Interface operated by Entertainment Trading Technologies Ltd., you agree to comply with and be bound by these Terms of Use. If you do not agree to these terms, please do not use the Interface.'
	},
	{
		title: '2. Website Purpose',
		text: 'This Interface offers a gateway for eligible users to claim the airdrop of PEPPER.'
	},
	{
		title: '3. Disclaimer of Liability',
		text: (
			<Section>
				<p>
					3.1 Entertainment Trading Technologies Ltd., incorporated in Seychelles is not the issuer of PEPPER
					and has no control over the token, its issuance, or distribution.
				</p>
				<p>
					3.2 We expressly disclaim any and all liability related to: a) The PEPPER token, its value,
					performance, or any outcomes associated with its use or ownership; b) The token distribution
					process, including but not limited to the airdrop mechanism; c) Any technical issues, delays, or
					failures in the claiming process; d) The accuracy, completeness, or reliability of any information
					provided about the token or the airdrop; e) Any decisions made by users based on the information
					provided on this website or in the Whitepepper document.
				</p>
				<p>
					3.3 Users acknowledge and agree that they are claiming the PEPPER token airdrop at their own risk
					and discretion.
				</p>
			</Section>
		)
	},
	{
		title: '4. User Responsibilities',
		text: (
			<Section>
				<p>
					4.1 Users are solely responsible for: a) Verifying their eligibility to claim the PEPPER token
					airdrop; b) Ensuring the accuracy and completeness of any information they provide during the
					claiming process; c) Securing and maintaining their own digital wallets and private keys; d)
					Complying with all applicable laws and regulations in their jurisdiction regarding the acquisition,
					holding, and use of cryptocurrencies.
				</p>
				<p>
					4.2 Users must not attempt to claim the airdrop if they are not eligible or if doing so would
					violate any laws or regulations in their jurisdiction.
				</p>
				<p>
					4.3 By using this Interface, users acknowledge that they understand and accept the risks associated
					with cryptocurrencies. Users are strongly advised to conduct their own research and consult with
					financial and legal professionals before engaging in any cryptocurrency-related activities.
				</p>
				<p>
					4.4 You acknowledge that Entertainment Trading Technologies Ltd. solely provides a gateway for the
					PEPPER token airdrop and does not hold, control, or manage users’ private keys, wallets, or assets.
					All transactions, including claiming tokens, occur directly between the user and the blockchain, and
					users bear full responsibility for managing their digital assets and the associated risks, including
					technical issues or financial loss. Entertainment Trading Technologies Ltd. does not intermediate or
					take responsibility for any part of the transaction process.
				</p>
			</Section>
		)
	},
	{
		title: '5. General Disclaimer',
		text: (
			<Section>
				<p>
					5.1 By using this website, you warrant and acknowledge that you are in full compliance with all
					local laws and regulations of the jurisdiction in which you reside or from which you access this
					website.
				</p>
				<p>
					5.2 You agree to indemnify and hold harmless Entertainment Trading Technologies Ltd., its officers,
					directors, employees, and agents from any claims, damages, or expenses arising from your use of this
					Interface in violation of these Terms of Use or any applicable laws or regulations.
				</p>
			</Section>
		)
	},
	{
		title: '6. Intellectual Property',
		text: (
			<Section>
				<p>
					6.1 All content on this Interface, including but not limited to text, graphics, logos, and software,
					is the property of Entertainment Trading Technologies Ltd. or its licensors and is protected by
					intellectual property laws.
				</p>
				<p>
					6.2 The intellectual property related to the PEPPER token is not the property of Entertainment
					Trading Technologies Ltd..
				</p>
				<p>
					6.3 Users are granted a limited, non-exclusive, non-transferable license to access and use the
					Interface content for personal, non-commercial purposes only.
				</p>
			</Section>
		)
	},
	{
		title: '7. No Investment Advice',
		text: 'Nothing contained on this Interface or in the Whitepepper constitutes financial, legal, or investment advice. Entertainment Trading Technologies Ltd. is not acting as a financial advisor or fiduciary of any person or entity. All content provided on this Interface is for informational purposes only. You should consult with qualified professionals before making any investment decisions.'
	},
	{
		title: '8. Third-party websites and services',
		text: (
			<Section>
				<p>
					The Interface may contain links to external websites and third-party interfaces which are not
					governed by these Terms of Use or any other policies available on the Interface provided by
					Entertainment Trading Technologies Ltd. By accessing these third-party websites or interfaces, you
					acknowledge that:
				</p>
				<ol className="flex flex-col gap-2 list-decimal pl-10 py-3 [&>li]:pl-2">
					<li>
						Each third-party website or interface may have its own terms and conditions, privacy policies,
						and security practices.
					</li>
					<li>
						Third-party websites or interfaces may carry inherent risks, including but not limited to
						security vulnerabilities, data protection issues, and financial risks associated with any
						products or services offered through them.
					</li>
					<li>
						Entertainment Trading Technologies Ltd. does not endorse, guarantee, or assume responsibility
						for the accuracy, completeness, or legality of any information, content, products, or services
						provided through these third-party websites or interfaces. You agree that Entertainment Trading
						Technologies Ltd. shall not be held liable for any loss, damage, or other issues arising from
						your use of or reliance on such third-party services, or from any transactions or interactions
						you engage in with these third parties.
					</li>
				</ol>
				<p>
					By using and accessing any links available through the Interface, you accept that any engagement
					with these third-party websites or interfaces is at your own discretion and risk.
				</p>
			</Section>
		)
	},
	{
		title: '9. Force Majeure',
		text: 'Entertainment Trading Technologies Ltd. shall not be liable for any failure of or delay in the performance of these Terms of Use for the period that such failure or delay is due to causes beyond its reasonable control, including but not limited to acts of God, war, strikes or labor disputes, embargoes, government orders, or any other force majeure event.'
	},
	{
		title: '10. Modifications to Terms',
		text: 'We reserve the right to modify these Terms of Use at any time. Any changes will be effective immediately upon posting on this website. Your continued use of the website after any such changes constitutes your acceptance of the new Terms of Use.'
	},
	{
		title: '11. Severability',
		text: 'If any provision of these Terms of Use is found to be invalid, illegal, or unenforceable, the remaining provisions shall continue in full force and effect. The invalid or unenforceable provision shall be replaced by a valid, legal, and enforceable provision that comes closest to the intention of the original provision.'
	},
	{
		title: '12. Waiver',
		text: 'The failure of Entertainment Trading Technologies Ltd. to exercise or enforce any right or provision of these Terms of Use shall not constitute a waiver of such right or provision. Any waiver of any provision of these Terms of Use will be effective only if in writing and signed by Entertainment Trading Technologies Ltd.'
	},
	{
		title: '13. Privacy and Data Protection',
		text: 'Entertainment Trading Technologies Ltd. respects your privacy and is committed to protecting your personal data. Our collection and use of your information is governed by our Privacy Policy, which can be found at https://www.peppercoin.com/privacy-policy. By using this Interface, you consent to the collection and use of your information as described in the Privacy Policy.'
	},
	{
		title: '14. Class Action Waiver',
		text: 'You agree that any dispute arising from or relating to these Terms or your use of the Interface shall be resolved on an individual basis, and you hereby waive any right to participate in a class action, collective action, or any other representative proceeding of any kind.'
	},
	{
		title: '15. Prohibited Activities',
		text: (
			<>
				<p>Users are strictly prohibited from engaging in any of the following activities:</p>
				<ol className="flex flex-col gap-2 list-decimal pl-10 py-3 [&>li]:pl-2">
					<li>
						Attempting to access, tamper with, or use non-public areas of the Interface, our computer
						systems, or the technical delivery systems of our providers.
					</li>
					<li>
						Probing, scanning, or testing the vulnerability of any system or network, or breaching or
						circumventing any security or authentication measures.
					</li>
					<li>
						Accessing or searching the Interface by any means other than our publicly supported Interfaces.
					</li>
					<li>Sending unsolicited communications, promotions, or advertisements, or spam.</li>
					<li>
						Sending altered, deceptive, or false source-identifying information, including "spoofing" or
						"phishing."
					</li>
					<li>
						Using the Interface for any illegal purpose or in violation of any applicable local, state,
						national, or international law.
					</li>
					<li>
						Interfering with, or attempting to interfere with, the access of any user, host, or network,
						including sending a virus, overloading, flooding, spamming, or mail-bombing the Interface.
					</li>
				</ol>
				<p>
					Violation of these prohibitions may result in termination of your access to the Interface and may
					subject you to civil and/or criminal penalties.
				</p>
			</>
		)
	},
	{
		title: '16. Governing Law and Jurisdiction',
		text: (
			<>
				<p>
					These Terms of Use shall be governed by and construed in accordance with the laws of the Republic of
					Seychelles. Any disputes arising from or relating to these terms or your use of the Interface shall
					be subject to the exclusive jurisdiction of the courts in Mahe, Seychelles.
				</p>
				<p>
					Furthermore, you irrevocably waive your right to a jury trial in any legal proceedings arising from
					or related to these Terms or your use of the Interface.
				</p>
			</>
		)
	},
	{
		title: '17. Contact Information',
		text: (
			<p>
				If you have any questions about these Terms of Use, please contact us through our official Telegram
				group{' '}
				<a href="https://t.me/officialpeppercoin" target="_blank" className="underline">
					https://t.me/officialpeppercoin
				</a>
				.
			</p>
		)
	},
	{
		text: <Divider variant="subtle" />
	},
	{
		text: 'By using this Interface, you acknowledge that you have read, understood, and agree to be bound by these Terms of Use.'
	}
]

const TermsAndConditions = () => (
	<div>
		<DocumentTemplate
			title="Terms and Conditions"
			contentBuckets={[
				{
					className: 'bg-green-10',
					bucketTitle: 'Terms of Use for Peppercoin.com',
					lastUpdated: 'November 4, 2024',
					content: CONTENT
				}
			]}
		/>
	</div>
)

export default TermsAndConditions
